import { AlertCircle, CheckCircle, Clock, FileCheck, MoreHorizontal, RotateCcw, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { apiUrl } from '@/config/api';
import { useToast } from '@/hooks/use-toast';

const API_BASE = apiUrl('/api/approvals');

interface Approval {
  id: string;
  applicationId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'RETURNED';
  reviewerName: string | null;
  comment: string | null;
  approvalType: 'DOCUMENT' | 'PAYMENT' | 'FINAL';
  approvedAt: string | null;
  createdAt: string;
  applicantName?: string;
  examName?: string;
}

type ActionType = 'approve' | 'reject' | 'return';

export default function ApprovalQueue() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  const [actionType, setActionType] = useState<ActionType>('approve');
  const [comment, setComment] = useState('');

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}?status=PENDING`);
      if (res.ok) {
        const data = (await res.json()) as Approval[];
        setApprovals(data);
      }
    } catch (error) {
      console.error('Failed to fetch approvals', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const openActionDialog = (approval: Approval, action: ActionType) => {
    setSelectedApproval(approval);
    setActionType(action);
    setComment('');
    setDialogOpen(true);
  };

  const executeAction = async () => {
    if (!selectedApproval) return;

    try {
      const res = await fetch(`${API_BASE}/${selectedApproval.id}/${actionType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment }),
      });

      if (res.ok) {
        toast({
          title: t(`modules.approvals.actions.${actionType}Success`),
          description: selectedApproval.applicantName,
        });
        setDialogOpen(false);
        fetchApprovals();
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: String(error),
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <Badge variant="secondary" className="gap-1 bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" />
            {t('modules.approvals.statuses.PENDING')}
          </Badge>
        );
      case 'APPROVED':
        return (
          <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            {t('modules.approvals.statuses.APPROVED')}
          </Badge>
        );
      case 'REJECTED':
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="w-3 h-3" />
            {t('modules.approvals.statuses.REJECTED')}
          </Badge>
        );
      case 'RETURNED':
        return (
          <Badge variant="outline" className="gap-1 border-orange-400 text-orange-600">
            <RotateCcw className="w-3 h-3" />
            {t('modules.approvals.statuses.RETURNED')}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('modules.approvals.title')}</h1>
          <p className="text-gray-500 mt-1">{t('modules.approvals.description')}</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2 bg-yellow-50 border-yellow-400 text-yellow-700">
          <AlertCircle className="w-4 h-4 mr-2" />
          {approvals.length} {t('modules.approvals.pendingCount')}
        </Badge>
      </div>

      {/* Queue Table */}
      <Card className="border-t-4 border-t-amber-500 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('modules.approvals.applicant')}</TableHead>
                <TableHead>{t('modules.approvals.exam')}</TableHead>
                <TableHead>{t('modules.approvals.type')}</TableHead>
                <TableHead>{t('modules.approvals.submittedAt')}</TableHead>
                <TableHead>{t('modules.approvals.status')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    {t('common.loading')}
                  </TableCell>
                </TableRow>
              ) : approvals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    <FileCheck className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    {t('modules.approvals.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                approvals.map((approval) => (
                  <TableRow key={approval.id} className="group hover:bg-gray-50">
                    <TableCell className="font-medium">{approval.applicantName || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-600">{approval.examName || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{t(`modules.approvals.types.${approval.approvalType}`)}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {new Date(approval.createdAt).toLocaleDateString('ja-JP')}
                    </TableCell>
                    <TableCell>{getStatusBadge(approval.status)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => openActionDialog(approval, 'approve')}
                            className="text-green-600"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" /> {t('modules.approvals.actions.approve')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openActionDialog(approval, 'return')}
                            className="text-orange-600"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" /> {t('modules.approvals.actions.return')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openActionDialog(approval, 'reject')}
                            className="text-red-600"
                          >
                            <XCircle className="w-4 h-4 mr-2" /> {t('modules.approvals.actions.reject')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`modules.approvals.actions.${actionType}Title`)}</DialogTitle>
            <DialogDescription>
              {selectedApproval?.applicantName} - {selectedApproval?.examName}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder={t('modules.approvals.commentPlaceholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={executeAction}
              variant={actionType === 'reject' ? 'destructive' : 'default'}
              className={
                actionType === 'approve'
                  ? 'bg-green-600 hover:bg-green-700'
                  : actionType === 'return'
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : ''
              }
            >
              {t(`modules.approvals.actions.${actionType}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
